import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8apq0kk00l6eltri10";

const contactChannels = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@asiacinema.example", hint: "内容合作与商务联系" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "AsiaCinema", hint: "每日推送影视动态" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@亚洲天堂影视", hint: "实时互动与话题讨论" },
  { icon: "ri-customer-service-2-line", label: "影迷反馈", value: "工作日 10:00 - 18:00", hint: "意见建议与投稿" },
];

export default function Contact() {
  useSeo({
    title: "联系我们 - 亚洲天堂",
    description:
      "欢迎联系亚洲天堂，获取合作邮箱与官方社交账号信息，或通过在线留言表单反馈你的建议、投稿与内容需求。",
    keywords: "联系亚洲天堂,影视合作,投稿,留言反馈",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      // Silently treat as success for bots
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
          <div className="mb-3">
            <Breadcrumb items={[{ label: "联系我们" }]} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">联系我们</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-500">
            无论你想投稿影评、提供线索，还是洽谈合作，都欢迎通过以下方式与我们取得联系。
          </p>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-site grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h2 className="mb-5 font-heading text-lg font-bold text-foreground-900">联系方式</h2>
          <div className="flex flex-col gap-4">
            {contactChannels.map((item) => (
              <div key={item.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-400">{item.label}</p>
                  <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{item.hint}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-4 text-xs leading-relaxed text-accent-800">
            <i className="ri-information-line mr-1" aria-hidden="true"></i>
            温馨提示：本站为演示站点，留言表单数据仅用于演示流程，联系方式信息为示例内容。
          </div>
        </div>

        <div className="lg:col-span-3">
          <h2 className="mb-5 font-heading text-lg font-bold text-foreground-900">在线留言</h2>
          <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <input
              type="text"
              name="website_alt"
              className="form-honeypot-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                留言内容 <span className="text-accent-600">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={6}
                placeholder="请填写你的建议、投稿或合作需求（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              ></textarea>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" aria-hidden="true"></i>
                    提交留言
                  </>
                )}
              </button>

              {status === "success" ? (
                <span className="flex items-center gap-1.5 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  提交成功，感谢你的留言，我们会尽快回复！
                </span>
              ) : null}
              {status === "error" ? (
                <span className="flex items-center gap-1.5 text-sm text-accent-700">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                  {formError}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}